import { useEffect, useState } from "react";

export type NumberFieldKind = "money" | "decimal" | "integer";

// money values are in cents; decimal/integer values are the plain unit (e.g. lb, count).
function parse(kind: NumberFieldKind, raw: string): number | null {
  if (raw.trim() === "") return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  return kind === "money" ? Math.round(n * 100) : n;
}

function display(kind: NumberFieldKind, value: number | null): string {
  if (value === null) return "";
  return kind === "money" ? (value / 100).toFixed(2) : String(value);
}

export default function NumberField({
  value, onCommit, kind, allowNull = false, ariaLabel, className, disabled,
}: {
  value: number | null;
  onCommit: (v: number | null) => void;
  kind: NumberFieldKind;
  allowNull?: boolean; // when true (Cost), an invalid/blank draft commits null; otherwise the last valid value is kept
  ariaLabel?: string;
  className?: string;
  disabled?: boolean;
}) {
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState(() => display(kind, value));

  // sync the draft from the outside world (a fresh session, a "raise to cost x1.25"
  // button, another field's edit) — but never fight the operator mid-keystroke.
  useEffect(() => {
    if (!focused) setDraft(display(kind, value));
  }, [value, focused, kind]);

  return (
    <input
      aria-label={ariaLabel}
      inputMode={kind === "integer" ? "numeric" : "decimal"}
      value={draft}
      disabled={disabled}
      onFocus={() => setFocused(true)}
      onChange={(e) => {
        const raw = e.target.value;
        setDraft(raw);
        const parsed = parse(kind, raw);
        if (parsed === null && !allowNull) return; // never store NaN — leave the last valid value in place
        onCommit(parsed);
      }}
      onBlur={() => {
        setFocused(false);
        const parsed = parse(kind, draft);
        if (parsed === null) {
          setDraft(display(kind, allowNull ? null : value));
          if (allowNull) onCommit(null);
          return;
        }
        setDraft(display(kind, parsed)); // normalise, e.g. "1.5" -> "1.50" for money
        onCommit(parsed);
      }}
      className={className}
    />
  );
}
